import { AntDesign } from "@expo/vector-icons";
import { BlurView } from "expo-blur";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { View, Modal, Pressable, Text, ActivityIndicator } from "react-native";
import { useDispatch } from "react-redux";

import ExportImage from "@/assets/exportbankstatement.svg";
import Title from "@/components/Title";
import Button from "@/components/button/Button";
import { setForm } from "@/context/actions/actions";
import { sendRequest } from "@/utils/ApiUtil";
import { generateStatement } from "@/utils/transactions/ExportBankStatement";
import { getTransactions } from "@/utils/transactions/GetTransactions";

interface PickDateModalProps {
  visible: boolean;
  onClick?: (state: boolean) => void;
  onClose: () => void;
  accountId: string;
  accountAssedId: string;
}

export default function PickDateModal(props: PickDateModalProps) {
  const [isFetching, setIsFetching] = useState(false);
  const [errorFetching, setErrorFetching] = useState(false);
  const [errorWithFileData, setErrorWithFileData] = useState(false);
  const [fetched, setFetched] = useState(false);
  const PAGE = 0;
  const PAGE_SIZE = 1000;

  const { t } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    if (isFetching) {
      dispatch(setForm({ isTransactionsExporting: true }));
    } else if (!isFetching) {
      dispatch(setForm({ isTransactionsExporting: false }));
    }
  }, [isFetching]);

  const clearStates = () => {
    setIsFetching(false);
    setFetched(false);
    setErrorFetching(false);
    setErrorWithFileData(false);
  };

  const handleExport = async () => {
    setIsFetching(true);
    setFetched(false);
    setErrorFetching(false);

    try {
      const fetchCustomer = await sendRequest(
        "GET",
        "/applications",
        null,
        true
      );
      let customerName: string = "";
      const application = fetchCustomer.application[0];

      if (application?.customerDetailsIndividual) {
        customerName = `${application.customerDetailsIndividual.firstName} ${application.customerDetailsIndividual.lastName}`;
      } else if (application?.customerDetailsCorporation) {
        customerName = application.customerDetailsCorporation.registeredName;
      }

      const fetchTransactions = await getTransactions(
        props.accountId,
        PAGE,
        PAGE_SIZE
      );
      if (fetchTransactions) {
        generateStatement(
          fetchTransactions.transactions,
          props.accountId,
          props.accountAssedId,
          t,
          customerName
        );
        setErrorWithFileData(fetchTransactions.error);
        setFetched(true);
      }
    } catch (error: any) {
      console.error(error);
      setErrorFetching(true);
    } finally {
      setIsFetching(false);
    }
  };

  return (
    <Modal
      visible={props.visible}
      onDismiss={() => clearStates()}
      transparent
      animationType="fade"
    >
      <BlurView intensity={20} className="flex-1 justify-center items-center">
        <View className="p-6 bg-white1 border border-gray rounded-lg w-full h-full items-center justify-center shadow relative lg:w-104 lg:h-136">
          <Pressable
            className="z-50 absolute right-3 top-3"
            onPress={props.onClose}
            disabled={isFetching}
          >
            <AntDesign name="close" size={24} color="black" />
          </Pressable>
          <View className="flex-grow items-center justify-center lg:justify-between">
            <View className="w-full m-2 gap-2 items-center">
              <Title text={t("home/modals/pickdate:title")} />
              <Text className="text-base text-darkergray text-center">
                {t("home/modals/pickdate:subtitle")}
              </Text>
            </View>
            <ExportImage className="w-40 h-40" />
            <View className="w-full items-center px-4 py-6">
              <Button
                onClick={() => handleExport()}
                label={t("common:button.download")}
              />
              <View className="w-full h-14 items-center">
                {isFetching && (
                  <View className="flex-row gap-2 items-center">
                    <ActivityIndicator size="small" color="#5D0CBA" animating />
                    <Text className="text-lg text-darkergray">
                      {t("home/modals/pickdate:generatingfile")}
                    </Text>
                  </View>
                )}
                {fetched && (
                  <View className="flex-col items-center">
                    <View className="flex-row gap-1 items-center">
                      <AntDesign name="check" size={24} color="green" />
                      <Text className="text-lg text-center text-darkergray">
                        {t("home/modals/pickdate:generatedfile")}
                      </Text>
                    </View>
                    {errorWithFileData && (
                      <Text className="text-xs text-center text-darkergray mt-1">
                        {t("home/modals/pickdate:fileincomplete")}
                      </Text>
                    )}
                  </View>
                )}
                {errorFetching && (
                  <View className="flex-row gap-2 items-center">
                    <AntDesign name="close" size={28} color="red" />
                    <Text className="text-lg text-darkergray">
                      {t("home/modals/pickdate:errorgeneratingfile")}
                    </Text>
                  </View>
                )}
              </View>
            </View>
          </View>
        </View>
      </BlurView>
    </Modal>
  );
}
